import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import FocusLock from "react-focus-lock";
import { useTranslation } from "react-i18next";

import PendingStockOperationNotice from "../../../components/stock/PendingStockOperationNotice";
import {
  applyStockMovementCorrection,
  previewStockMovementCorrection,
} from "../../../services/stockService";

function formatDecimal(value, digits = 6) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toFixed(digits).replace(/\.?0+$/, "");
}
function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(2)} PLN` : String(value);
}
function normalizeNumberInput(value) { return String(value ?? "").replace(",", ".").trim(); }
function locationLabel(locations, id, movement) {
  if (id == null || id === "") return "—";
  const row = locations.find((location) => String(location.id) === String(id));
  if (row) return row.name;
  if (String(movement?.from_location_id) === String(id)) return movement?.fromLocation?.name || `#${id}`;
  if (String(movement?.to_location_id) === String(id)) return movement?.toLocation?.name || `#${id}`;
  return `#${id}`;
}
function userDraft(movement) {
  return {
    quantity: String(movement?.quantity ?? ""),
    from_location_id: movement?.from_location_id == null ? "" : String(movement.from_location_id),
    to_location_id: movement?.to_location_id == null ? "" : String(movement.to_location_id),
    cost_entry_mode: movement?.cost_entry_mode || "stock_unit",
    unit_cost_net: movement?.unit_cost_net == null ? "" : String(movement.unit_cost_net),
    package_unit_cost_net: movement?.package_unit_cost_net == null ? "" : String(movement.package_unit_cost_net),
    total_cost_net: movement?.total_cost_net == null ? "" : String(movement.total_cost_net),
    batch_allocations: (movement?.batchAllocations || []).map((row) => ({
      id: String(row.id), batch_id: String(row.batch_id), quantity: String(row.quantity),
      label: row.batch?.batch_number || `#${row.batch_id}`,
    })),
    reason: "",
    comment: "",
  };
}
function payloadOf(draft, policy = {}) {
  const costEditable = Boolean(policy.editable?.cost);
  return {
    quantity: normalizeNumberInput(draft.quantity),
    from_location_id: draft.from_location_id || null,
    to_location_id: draft.to_location_id || null,
    // Outgoing/transfer valuation is planner-owned. Do not echo the displayed
    // effective cost back as user input, otherwise the backend correctly treats
    // it as an attempted direct cost edit.
    cost_entry_mode: costEditable ? (draft.cost_entry_mode || null) : null,
    unit_cost_net: costEditable ? (normalizeNumberInput(draft.unit_cost_net) || null) : null,
    package_unit_cost_net: costEditable ? (normalizeNumberInput(draft.package_unit_cost_net) || null) : null,
    total_cost_net: costEditable ? (normalizeNumberInput(draft.total_cost_net) || null) : null,
    batch_allocations: draft.batch_allocations.map(({ id, batch_id, quantity }) => ({
      id, batch_id, quantity: normalizeNumberInput(quantity),
    })),
    reason: draft.reason.trim(),
    comment: draft.comment.trim(),
  };
}
function DetailPreview({ label, value }) {
  return <div className="stock-audit-detail__field"><dt>{label}</dt><dd>{value ?? "—"}</dd></div>;
}
function locationOptions(locations, movement) {
  const map = new Map((locations || []).map((row) => [String(row.id), row]));
  for (const row of [movement?.fromLocation, movement?.toLocation].filter(Boolean)) {
    if (!map.has(String(row.id))) map.set(String(row.id), row);
  }
  return [...map.values()].sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
}

export default function StockMovementCorrectionDialog({ open, movement, locations = [], onClose, onApplied }) {
  const { t } = useTranslation(["stock", "common"]);
  const [draft, setDraft] = useState(() => userDraft(movement));
  const [preview, setPreview] = useState(null);
  const [previewFingerprint, setPreviewFingerprint] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const policy = movement?.correction_policy || {};
  const unit = movement?.stock_unit_code || movement?.product?.stock_unit_code || "";
  const options = useMemo(() => locationOptions(locations, movement), [locations, movement]);
  const fingerprint = JSON.stringify(payloadOf(draft, policy));
  const previewCurrent = Boolean(preview && previewFingerprint === fingerprint);

  useEffect(() => {
    if (!open || !movement) return;
    setDraft(userDraft(movement));
    setPreview(null);
    setPreviewFingerprint("");
    setError("");
  }, [open, movement]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const key = (event) => {
      if (event.key === "Escape" && !busy) { event.preventDefault(); onClose?.(); }
    };
    document.addEventListener("keydown", key);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener("keydown", key); };
  }, [open, busy, onClose]);

  if (!open || !movement) return null;

  const set = (field, value) => {
    setDraft((current) => {
      const next = { ...current, [field]: value };
      if (field === "quantity" && current.batch_allocations.length === 1) {
        next.batch_allocations = [{ ...current.batch_allocations[0], quantity: value }];
      }
      return next;
    });
    setPreview(null); setPreviewFingerprint(""); setError("");
  };
  const setAllocation = (index, quantity) => {
    setDraft((current) => ({ ...current, batch_allocations: current.batch_allocations.map((row, i) => i === index ? { ...row, quantity } : row) }));
    setPreview(null); setPreviewFingerprint(""); setError("");
  };
  const translatedError = (err) => t(`stock:movementCorrection.codes.${err?.code}`, { defaultValue: err?.data?.message || err?.message || t("common:errors.actionFailed") });

  async function runPreview() {
    setBusy(true); setError("");
    try {
      const body = payloadOf(draft, policy);
      const result = await previewStockMovementCorrection(movement.id, body);
      setPreview(result); setPreviewFingerprint(JSON.stringify(body));
    } catch (err) { setError(translatedError(err)); }
    finally { setBusy(false); }
  }
  async function apply() {
    if (!previewCurrent) return;
    setBusy(true); setError("");
    try {
      const result = await applyStockMovementCorrection(movement.id, payloadOf(draft, policy));
      await onApplied?.(result);
    } catch (err) { setError(translatedError(err)); }
    finally { setBusy(false); }
  }
  async function recovered(result) { await onApplied?.(result); }

  const allocationSum = draft.batch_allocations.reduce((sum, row) => sum + (Number(normalizeNumberInput(row.quantity)) || 0), 0);
  const quantityNumber = Number(normalizeNumberInput(draft.quantity));
  const allocationMismatch = draft.batch_allocations.length > 0 && Number.isFinite(quantityNumber) && Math.abs(allocationSum - quantityNumber) > 0.0000005;
  const costModes = movement.movement_type === "receipt"
    ? ["stock_unit", ...(movement.package_stock_quantity_per_package ? ["package"] : []), "total"]
    : ["stock_unit"];

  return createPortal(
    <div className="stock-dialog-backdrop" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
      <FocusLock returnFocus>
        <div className="stock-audit-dialog stock-correction-dialog" role="dialog" aria-modal="true" aria-labelledby="stock-correction-title">
          <header className="stock-audit-dialog__header">
            <div>
              <span className="stock-dialog__eyebrow">{t("stock:movementCorrection.eyebrow")}</span>
              <h2 id="stock-correction-title">{t("stock:movementCorrection.title", { id: movement.id })}</h2>
              <p>{t("stock:movementCorrection.subtitle")}</p>
            </div>
            <button type="button" className="stock-dialog__close" disabled={busy} onClick={onClose} aria-label={t("common:actions.close")}>×</button>
          </header>

          <div className="stock-audit-dialog__body">
            <PendingStockOperationNotice scope="stock" onResolved={recovered} />
            {error && <div className="stock-alert stock-alert--error">{error}</div>}

            <section className="stock-correction-notice">
              <strong>{t("stock:movementCorrection.safetyTitle")}</strong>
              <span>{t("stock:movementCorrection.safetyText", { date: movement.business_date })}</span>
            </section>

            <div className="stock-correction-summary">
              <div><span>{t("stock:audit.product")}</span><strong>{movement.product?.name_public || `#${movement.product_id}`}</strong></div>
              <div><span>{t("stock:audit.type")}</span><strong>{t(`stock:actions.${movement.movement_type}`, { defaultValue: movement.movement_type })}</strong></div>
              <div><span>{t("stock:audit.businessDate")}</span><strong>{movement.business_date}</strong></div>
              <div><span>{t("stock:audit.stockUnit")}</span><strong>{unit || "—"}</strong></div>
            </div>

            <section className="stock-audit-detail__section">
              <h3>{t("stock:movementCorrection.fieldsTitle")}</h3>
              <div className="stock-correction-grid">
                <label className="stock-form-field">
                  {t("stock:movementCorrection.quantity")}
                  <input type="text" inputMode="decimal" value={draft.quantity} onChange={(e) => set("quantity", e.target.value)} />
                  <small>{unit}</small>
                </label>

                {policy.editable?.from_location && <label className="stock-form-field">
                  {t("stock:movementCorrection.fromLocation")}
                  <select value={draft.from_location_id} onChange={(e) => set("from_location_id", e.target.value)}>
                    {options.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
                  </select>
                </label>}
                {policy.editable?.to_location && <label className="stock-form-field">
                  {t("stock:movementCorrection.toLocation")}
                  <select value={draft.to_location_id} onChange={(e) => set("to_location_id", e.target.value)}>
                    {options.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
                  </select>
                </label>}

                {policy.editable?.cost && <>
                  <label className="stock-form-field">
                    {t("stock:movementCorrection.costMode")}
                    <select value={draft.cost_entry_mode} onChange={(e) => set("cost_entry_mode", e.target.value)}>
                      {costModes.map((mode) => <option key={mode} value={mode}>{t(`stock:costing.mode${mode === "stock_unit" ? "StockUnit" : mode === "package" ? "Package" : "Total"}`)}</option>)}
                    </select>
                  </label>
                  {draft.cost_entry_mode === "stock_unit" && <label className="stock-form-field">
                    {t("stock:movementCorrection.unitCost")}
                    <input type="text" inputMode="decimal" value={draft.unit_cost_net} onChange={(e) => set("unit_cost_net", e.target.value)} />
                    <small>PLN/{unit}</small>
                  </label>}
                  {draft.cost_entry_mode === "package" && <label className="stock-form-field">
                    {t("stock:movementCorrection.packageCost")}
                    <input type="text" inputMode="decimal" value={draft.package_unit_cost_net} onChange={(e) => set("package_unit_cost_net", e.target.value)} />
                    <small>{movement.package_label_snapshot || t("stock:packages.defaultPackageLabel")}</small>
                  </label>}
                  {draft.cost_entry_mode === "total" && <label className="stock-form-field">
                    {t("stock:movementCorrection.totalCost")}
                    <input type="text" inputMode="decimal" value={draft.total_cost_net} onChange={(e) => set("total_cost_net", e.target.value)} />
                    <small>PLN</small>
                  </label>}
                </>}
              </div>
            </section>

            {draft.batch_allocations.length > 0 && <section className="stock-audit-detail__section">
              <h3>{t("stock:movementCorrection.lotsTitle")}</h3>
              <p>{t("stock:movementCorrection.lotsHelp")}</p>
              <div className="stock-correction-lots">
                {draft.batch_allocations.map((row, index) => <label key={row.id} className="stock-correction-lot-row">
                  <span>{row.label}</span>
                  <input type="text" inputMode="decimal" value={row.quantity} onChange={(e) => setAllocation(index, e.target.value)} />
                  <small>{unit}</small>
                </label>)}
              </div>
              <div className={allocationMismatch ? "stock-correction-allocation stock-correction-allocation--error" : "stock-correction-allocation"}>
                {t("stock:movementCorrection.lotSum", { sum: formatDecimal(allocationSum, 6), quantity: formatDecimal(quantityNumber, 6), unit })}
              </div>
            </section>}

            <section className="stock-audit-detail__section">
              <h3>{t("stock:movementCorrection.auditTitle")}</h3>
              <div className="stock-correction-grid">
                <label className="stock-form-field">
                  {t("stock:movementCorrection.reason")}
                  <input type="text" maxLength="120" value={draft.reason} onChange={(e) => set("reason", e.target.value)} />
                </label>
                <label className="stock-form-field stock-correction-field--wide">
                  {t("stock:movementCorrection.comment")}
                  <textarea rows="3" maxLength="4000" value={draft.comment} onChange={(e) => set("comment", e.target.value)} />
                </label>
              </div>
            </section>

            {previewCurrent && preview && <section className="stock-audit-detail__section stock-correction-preview">
              <h3>{t("stock:movementCorrection.previewTitle")}</h3>
              <div className="stock-correction-compare">
                <div><span>{t("stock:movementCorrection.metric")}</span><strong>{t("stock:movementCorrection.before")}</strong><strong>{t("stock:movementCorrection.after")}</strong></div>
                <div><span>{t("stock:movementCorrection.quantity")}</span><strong>{formatDecimal(preview.before?.quantity, 6)} {unit}</strong><strong>{formatDecimal(preview.after?.quantity, 6)} {unit}</strong></div>
                <div><span>{t("stock:movementCorrection.unitCost")}</span><strong>{preview.before?.unit_cost_net} PLN/{unit}</strong><strong>{preview.after?.unit_cost_net} PLN/{unit}</strong></div>
                <div><span>{t("stock:movementCorrection.movementValue")}</span><strong>{formatMoney(preview.before?.total_cost_net)}</strong><strong>{formatMoney(preview.after?.total_cost_net)}</strong></div>
                <div><span>{t("stock:movementCorrection.inventoryValue")}</span><strong>{formatMoney(preview.impact?.current_before?.total_value_net)}</strong><strong>{formatMoney(preview.impact?.current_after?.total_value_net)}</strong></div>
              </div>
              {preview.value_adjustment && (
                <div className="stock-correction-notice stock-correction-notice--wom">
                  <strong>{t("stock:valueAdjustment.detailHistorical")}</strong>
                  <span>{t("stock:valueAdjustment.historicalPostingNotice")}</span>
                  <dl className="stock-audit-detail__grid">
                    <DetailPreview label={t("stock:valueAdjustment.historicalValueDelta")} value={formatMoney(preview.value_adjustment.value_delta_net)} />
                    <DetailPreview label={t("stock:valueAdjustment.currentEffectiveSourceValue")} value={formatMoney(preview.value_adjustment.current_effective_source_value_net)} />
                    <DetailPreview label={t("stock:valueAdjustment.targetEffectiveSourceValue")} value={formatMoney(preview.value_adjustment.target_effective_source_value_net)} />
                  </dl>
                </div>
              )}
              <p>{t("stock:movementCorrection.replayImpact", { replayed: preview.impact?.replayed_movements || 0, revalued: preview.impact?.revalued_movements || 0 })}</p>
            </section>}
          </div>

          <footer className="stock-audit-dialog__footer stock-correction-footer">
            <button type="button" className="stock-btn stock-btn--secondary" onClick={onClose} disabled={busy}>{t("common:actions.cancel")}</button>
            <button type="button" className="stock-btn stock-btn--outline" onClick={runPreview} disabled={busy || allocationMismatch || !draft.reason.trim() || !draft.comment.trim()}>
              {busy ? t("common:common.loading") : t("stock:movementCorrection.preview")}
            </button>
            <button type="button" className="stock-btn stock-btn--primary" onClick={apply} disabled={busy || !previewCurrent}>
              {busy ? t("common:common.loading") : t("stock:movementCorrection.apply")}
            </button>
          </footer>
        </div>
      </FocusLock>
    </div>,
    document.body
  );
}
